import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  ClipboardList, 
  Search, 
  Filter, 
  User, 
  Clock, 
  Calendar,
  CheckCircle2,
  Circle,
  ChevronDown,
  ChevronUp,
  UserPlus,
  X,
  CheckCheck,
  RefreshCw,
  Phone,
  Stethoscope
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { format } from 'date-fns';

const JobCardsPage = ({ user, onLogout }) => {
  const [jobCards, setJobCards] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDate, setFilterDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [filterTherapist, setFilterTherapist] = useState('all');
  const [filterService, setFilterService] = useState('all');
  const [expandedCard, setExpandedCard] = useState(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedJobCard, setSelectedJobCard] = useState(null);
  const [selectedTherapists, setSelectedTherapists] = useState([]);

  // Therapist + Teller use the mobile card view; Teller additionally gets search, therapist filter and assign.
  const isTherapist = user?.role === 'Therapist';
  const isTeller = user?.role === 'Teller';
  const isMobileView = isTherapist || isTeller;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build query params
      const params = new URLSearchParams();
      if (filterDate && filterDate !== 'all') params.append('date', filterDate);
      if (filterStatus && filterStatus !== 'all') params.append('status', filterStatus);
      if (filterTherapist && filterTherapist !== 'all') params.append('therapist_id', filterTherapist);
      if (filterService && filterService !== 'all') params.append('service_id', filterService);
      if (searchQuery) params.append('patient_name', searchQuery);

      const [jobCardsRes, therapistsRes, servicesRes] = await Promise.all([
        api.get(`/job-cards?${params.toString()}`),
        api.get('/therapists'),
        api.get('/services')
      ]);

      setJobCards(jobCardsRes.data);
      setTherapists(therapistsRes.data);
      setServices(servicesRes.data.filter(s => s.type === 'time_based'));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch job cards');
    } finally {
      setLoading(false);
    }
  }, [filterDate, filterStatus, filterTherapist, filterService, searchQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'completed':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'cancelled':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-3 h-3" />;
      case 'in_progress':
        return <RefreshCw className="w-3 h-3" />;
      case 'completed':
        return <CheckCircle2 className="w-3 h-3" />;
      case 'cancelled':
        return <X className="w-3 h-3" />;
      default:
        return <Circle className="w-3 h-3" />;
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    try {
      return new Date(isoString).toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return isoString;
    }
  };

  const handleAssignTherapist = (jobCard) => {
    setSelectedJobCard(jobCard);
    setSelectedTherapists(jobCard.assigned_therapist_ids || []);
    setAssignModalOpen(true);
  };

  const handleSaveAssignment = async () => {
    if (!selectedJobCard) return;

    try {
      await api.put(`/job-cards/${selectedJobCard.id}`, {
        assigned_therapist_ids: selectedTherapists
      });
      toast.success('Therapists assigned successfully');
      setAssignModalOpen(false);
      setSelectedJobCard(null);
      setSelectedTherapists([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to assign therapists');
    }
  };

  const toggleTherapistSelection = (therapistId) => {
    setSelectedTherapists(prev => 
      prev.includes(therapistId)
        ? prev.filter(id => id !== therapistId)
        : [...prev, therapistId]
    );
  };

  const handleCompleteChecklist = async (jobCardId, itemId) => {
    try {
      await api.post(`/job-cards/${jobCardId}/checklist/${itemId}/complete`);
      toast.success('Checklist item completed');
      fetchData();
    } catch (error) {
      toast.error('Failed to complete checklist item');
    }
  };

  const handleCompleteStep = async (jobCardId, stepId) => {
    try {
      const response = await api.post(`/job-cards/${jobCardId}/steps/${stepId}/complete`);
      if (response.data.job_card_completed) {
        toast.success('All steps done - Job Card closed! Patient checked out 🎉');
      } else {
        toast.success('Step completed ✓');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete step');
    }
  };

  const handleCloseJobCard = async (jobCardId) => {
    try {
      await api.post(`/job-cards/${jobCardId}/close`);
      toast.success('Job Card closed! Patient checked out 🎉');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close job card');
    }
  };

  const assignDialog = (
    <>
      {/* Assign Therapist Modal */}
      <Dialog open={assignModalOpen} onOpenChange={setAssignModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-purple-600" />
              Assign Therapists
            </DialogTitle>
            <DialogDescription>
              Select therapists to assign to this job card
            </DialogDescription>
          </DialogHeader>

          {selectedJobCard && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="font-medium text-gray-900">{selectedJobCard.patient_name}</div>
              <div className="text-sm text-gray-500">{selectedJobCard.service_name}</div>
            </div>
          )}

          <div className="space-y-2 max-h-60 overflow-y-auto">
            {therapists.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">
                No therapists available. Create therapist users first.
              </p>
            ) : (
              therapists.map(therapist => (
                <div
                  key={therapist.id}
                  className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition ${
                    selectedTherapists.includes(therapist.id)
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => toggleTherapistSelection(therapist.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      selectedTherapists.includes(therapist.id)
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      <User className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{therapist.name}</div>
                      <div className="text-xs text-gray-500">{therapist.username}</div>
                    </div>
                  </div>
                  {selectedTherapists.includes(therapist.id) && (
                    <CheckCircle2 className="w-5 h-5 text-purple-600" />
                  )}
                </div>
              ))
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveAssignment} disabled={selectedTherapists.length === 0}>
              Assign {selectedTherapists.length > 0 && `(${selectedTherapists.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  // ==================== THERAPIST / TELLER MOBILE VIEW ====================
  if (isMobileView) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="px-4 py-3" data-testid="jobcards-mobile-view">
          {/* Date: Teller is locked to today; Therapist can browse days */}
          {isTeller ? (
            <div className="text-center mb-3" data-testid="jobcards-today-label">
              <p className="font-semibold text-gray-900 text-lg">{format(new Date(), 'd MMM yyyy')}</p>
              <p className="text-xs text-gray-500">Today · {format(new Date(), 'EEEE')}</p>
            </div>
          ) : (
          <div className="flex items-center justify-between mb-3">
            <button 
              onClick={() => {
                const d = new Date(filterDate);
                d.setDate(d.getDate() - 1);
                setFilterDate(d.toISOString().split('T')[0]);
              }}
              className="p-2 rounded-full hover:bg-gray-100 active:bg-gray-200"
            >
              <ChevronUp className="w-5 h-5 text-gray-600 rotate-[-90deg]" />
            </button>
            <div className="text-center flex-1">
              <input
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="bg-transparent text-center font-semibold text-gray-900 border-none focus:outline-none text-lg"
              />
              <p className="text-xs text-gray-500">
                {format(new Date(filterDate), 'EEEE')}
              </p>
            </div>
            <button 
              onClick={() => {
                const d = new Date(filterDate);
                d.setDate(d.getDate() + 1);
                setFilterDate(d.toISOString().split('T')[0]);
              }}
              className="p-2 rounded-full hover:bg-gray-100 active:bg-gray-200"
            >
              <ChevronDown className="w-5 h-5 text-gray-600 rotate-[-90deg]" />
            </button>
          </div>
          )}

          {/* Status Pills */}
          <div className="flex gap-2 mb-4 overflow-x-auto pb-1 -mx-4 px-4">
            {[
              { key: 'all', label: 'All', color: 'gray' },
              { key: 'in_progress', label: 'Active', color: 'blue' },
              { key: 'pending', label: 'Waiting', color: 'yellow' },
              { key: 'completed', label: 'Done', color: 'green' }
            ].map(({ key, label, color }) => {
              const count = key === 'all' 
                ? jobCards.length 
                : jobCards.filter(jc => jc.status === key).length;
              const isActive = filterStatus === key;
              return (
                <button
                  key={key}
                  onClick={() => setFilterStatus(key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                    isActive 
                      ? `bg-${color}-500 text-white shadow-sm` 
                      : `bg-${color}-50 text-${color}-700 border border-${color}-200`
                  }`}
                  style={{
                    backgroundColor: isActive 
                      ? (color === 'gray' ? '#6b7280' : color === 'blue' ? '#3b82f6' : color === 'yellow' ? '#eab308' : '#22c55e')
                      : undefined
                  }}
                >
                  {label}
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    isActive ? 'bg-white/20' : `bg-${color}-100`
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {isTeller && (
            <div className="flex gap-2 mb-4">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search patient"
                  className="pl-9 h-11 rounded-xl"
                  data-testid="jobcards-mobile-search"
                />
              </div>
              <Select value={filterTherapist} onValueChange={setFilterTherapist}>
                <SelectTrigger className="w-[150px] h-11 rounded-xl" data-testid="jobcards-mobile-therapist-filter">
                  <SelectValue placeholder="Therapist" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All therapists</SelectItem>
                  {therapists.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Job Cards List */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="w-8 h-8 text-purple-500 animate-spin" />
            </div>
          ) : jobCards.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ClipboardList className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500 font-medium">No job cards</p>
              <p className="text-sm text-gray-400 mt-1">Pull down to refresh</p>
            </div>
          ) : (
            <div className="space-y-3 pb-6">
              {jobCards.map(jobCard => {
                const isExpanded = expandedCard === jobCard.id;
                const checklistDone = jobCard.pre_procedure_checklist?.filter(i => i.is_completed).length || 0;
                const checklistTotal = jobCard.pre_procedure_checklist?.length || 0;
                const stepsDone = jobCard.procedure_steps?.filter(s => s.is_completed).length || 0;
                const stepsTotal = jobCard.procedure_steps?.length || 0;
                const totalDone = checklistDone + stepsDone;
                const totalItems = checklistTotal + stepsTotal;
                const progress = totalItems > 0 ? (totalDone / totalItems) * 100 : 0;

                return (
                  <div
                    key={jobCard.id}
                    className={`bg-white rounded-2xl shadow-sm border overflow-hidden transition-all ${
                      jobCard.status === 'in_progress' 
                        ? 'border-blue-200 shadow-blue-100' 
                        : jobCard.status === 'completed'
                          ? 'border-green-200'
                          : 'border-gray-200'
                    }`}
                  >
                    {/* Card Header - Tappable */}
                    <div 
                      className="p-4 active:bg-gray-50"
                      onClick={() => setExpandedCard(isExpanded ? null : jobCard.id)}
                    >
                      {/* Top Row: Status & Progress */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge className={`${getStatusColor(jobCard.status)} text-xs px-2 py-0.5`}>
                            {jobCard.status === 'in_progress' ? 'Active' : 
                             jobCard.status === 'pending' ? 'Waiting' :
                             jobCard.status === 'completed' ? 'Done' : jobCard.status}
                          </Badge>
                          {jobCard.is_paid ? (
                            <Badge className="bg-green-100 text-green-700 text-xs px-2 py-0.5">
                              ✓ Paid
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5">
                              Payment Pending
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-gray-700">
                            {totalDone}/{totalItems}
                          </span>
                          <div className="w-12 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${
                                jobCard.status === 'completed' ? 'bg-green-500' : 'bg-purple-500'
                              }`}
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Reference & Patient Info */}
                      {jobCard.appointment_ref_number && (
                        <span className="text-xs font-mono bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded mb-1 inline-block">
                          {jobCard.appointment_ref_number}
                        </span>
                      )}
                      <h3 className="font-semibold text-gray-900 text-lg leading-tight">
                        {jobCard.patient_name}
                      </h3>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-sm text-gray-500">{jobCard.service_name}</p>
                        {user?.role === 'Admin' && jobCard.payment_amount > 0 && (
                          <span className="text-xs font-semibold text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                            ₹{jobCard.payment_amount.toLocaleString()}
                          </span>
                        )}
                      </div>
                      
                      {/* Time + therapist */}
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {jobCard.scheduled_time || 'No time'}
                        </span>
                        {isTeller && (
                          jobCard.assigned_therapist_names?.length > 0 ? (
                            <span className="flex items-center gap-1 text-purple-600" data-testid={`jc-mobile-therapists-${jobCard.id}`}>
                              <User className="w-3.5 h-3.5" />
                              {jobCard.assigned_therapist_names.join(', ')}
                            </span>
                          ) : (
                            <span className="text-orange-600 font-medium" data-testid={`jc-mobile-unassigned-${jobCard.id}`}>Unassigned</span>
                          )
                        )}
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="border-t border-gray-100 bg-gray-50/50">
                        {isTeller && jobCard.status !== 'completed' && jobCard.status !== 'cancelled' && (
                          <div className="p-4 border-b border-gray-100">
                            <Button
                              variant="outline"
                              onClick={() => handleAssignTherapist(jobCard)}
                              className="w-full h-11 rounded-xl border-purple-300 text-purple-700 hover:bg-purple-50"
                              data-testid={`jc-mobile-assign-${jobCard.id}`}
                            >
                              <UserPlus className="w-4 h-4 mr-2" />
                              {jobCard.assigned_therapist_ids?.length > 0 ? 'Reassign therapist' : 'Assign therapist'}
                            </Button>
                          </div>
                        )}
                        {/* Checklist */}
                        {checklistTotal > 0 && (
                          <div className="p-4 border-b border-gray-100">
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                              Checklist ({checklistDone}/{checklistTotal})
                            </h4>
                            <div className="space-y-2">
                              {jobCard.pre_procedure_checklist.map(item => (
                                <div 
                                  key={item.id}
                                  className={`flex items-center gap-3 p-3 rounded-xl ${
                                    item.is_completed ? 'bg-green-50' : 'bg-white border border-gray-200'
                                  }`}
                                >
                                  {item.is_completed ? (
                                    <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                                  ) : (
                                    <button
                                      onClick={() => handleCompleteChecklist(jobCard.id, item.id)}
                                      disabled={jobCard.status !== 'in_progress'}
                                      className={`w-6 h-6 rounded-full border-2 flex-shrink-0 ${
                                        jobCard.status === 'in_progress' 
                                          ? 'border-purple-400 active:bg-purple-100' 
                                          : 'border-gray-300'
                                      }`}
                                    />
                                  )}
                                  <span className={`flex-1 text-sm ${
                                    item.is_completed ? 'text-green-700' : 'text-gray-700'
                                  }`}>
                                    {item.description}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Procedure Steps */}
                        {stepsTotal > 0 && (
                          <div className="p-4 border-b border-gray-100">
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                              Steps ({stepsDone}/{stepsTotal})
                            </h4>
                            <div className="space-y-2">
                              {jobCard.procedure_steps.map((step, stepIndex) => {
                                const canComplete = stepIndex === 0 || jobCard.procedure_steps[stepIndex - 1]?.is_completed;
                                
                                return (
                                  <div 
                                    key={step.id}
                                    className={`flex items-center gap-3 p-3 rounded-xl ${
                                      step.is_completed 
                                        ? 'bg-green-50' 
                                        : canComplete && jobCard.status === 'in_progress'
                                          ? 'bg-white border-2 border-purple-200'
                                          : 'bg-white border border-gray-200'
                                    }`}
                                  >
                                    {step.is_completed ? (
                                      <div className="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                                        <CheckCheck className="w-4 h-4 text-white" />
                                      </div>
                                    ) : (
                                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold ${
                                        canComplete && jobCard.status === 'in_progress'
                                          ? 'bg-purple-100 text-purple-700'
                                          : 'bg-gray-100 text-gray-500'
                                      }`}>
                                        {step.order}
                                      </div>
                                    )}
                                    <span className={`flex-1 text-sm ${
                                      step.is_completed ? 'text-green-700' : 'text-gray-700'
                                    }`}>
                                      {step.description}
                                      {step.time_taken_minutes !== undefined && step.time_taken_minutes !== null && (
                                        <span className="text-xs text-gray-400 ml-1">
                                          ({step.time_taken_minutes.toFixed(1)}m)
                                        </span>
                                      )}
                                    </span>
                                    {!step.is_completed && canComplete && jobCard.status === 'in_progress' && (
                                      <Button
                                        size="sm"
                                        className="bg-purple-600 hover:bg-purple-700 text-white px-4"
                                        onClick={() => handleCompleteStep(jobCard.id, step.id)}
                                      >
                                        Done
                                      </Button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Close Job Card Button */}
                        {jobCard.status === 'in_progress' && (
                          <div className="p-4">
                            <Button
                              onClick={() => handleCloseJobCard(jobCard.id)}
                              className="w-full bg-green-600 hover:bg-green-700 text-white h-12 text-base font-semibold rounded-xl"
                            >
                              <CheckCircle2 className="w-5 h-5 mr-2" />
                              Complete & Checkout
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {assignDialog}
      </Layout>
    );
  }

  // ==================== ADMIN/FRONT DESK VIEW ====================
  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ClipboardList className="w-7 h-7 text-purple-600" />
              Job Cards
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage and track service job cards
            </p>
          </div>
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-gray-500 mb-1 block">Search Patient</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Patient name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Date Filter */}
            <div className="w-40">
              <Label className="text-xs text-gray-500 mb-1 block">Date</Label>
              <Input
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
              />
            </div>

            {/* Status Filter */}
            <div className="w-40">
              <Label className="text-xs text-gray-500 mb-1 block">Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger>
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Therapist Filter */}
            <div className="w-48">
              <Label className="text-xs text-gray-500 mb-1 block">Therapist</Label>
              <Select value={filterTherapist} onValueChange={setFilterTherapist}>
                <SelectTrigger>
                  <User className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All Therapists" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Therapists</SelectItem>
                  {therapists.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Service Filter */}
            <div className="w-48">
              <Label className="text-xs text-gray-500 mb-1 block">Service</Label>
              <Select value={filterService} onValueChange={setFilterService}>
                <SelectTrigger>
                  <SelectValue placeholder="All Services" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Services</SelectItem>
                  {services.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Clear Filters */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchQuery('');
                setFilterDate(format(new Date(), 'yyyy-MM-dd'));
                setFilterStatus('all');
                setFilterTherapist('all');
                setFilterService('all');
              }}
            >
              Clear
            </Button>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
            <div className="text-2xl font-bold text-yellow-700">
              {jobCards.filter(jc => jc.status === 'pending').length}
            </div>
            <div className="text-sm text-yellow-600">Pending</div>
          </div>
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <div className="text-2xl font-bold text-blue-700">
              {jobCards.filter(jc => jc.status === 'in_progress').length}
            </div>
            <div className="text-sm text-blue-600">In Progress</div>
          </div>
          <div className="bg-green-50 rounded-xl p-4 border border-green-200">
            <div className="text-2xl font-bold text-green-700">
              {jobCards.filter(jc => jc.status === 'completed').length}
            </div>
            <div className="text-sm text-green-600">Completed</div>
          </div>
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <div className="text-2xl font-bold text-gray-700">
              {jobCards.length}
            </div>
            <div className="text-sm text-gray-600">Total</div>
          </div>
        </div>

        {/* Job Cards List */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading job cards...</div>
        ) : jobCards.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No job cards found</p>
            <p className="text-sm text-gray-400 mt-1">
              Job cards are created when patients check in for time-based services
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {jobCards.map(jobCard => {
              const isExpanded = expandedCard === jobCard.id;
              const checklistProgress = jobCard.pre_procedure_checklist?.length > 0
                ? jobCard.pre_procedure_checklist.filter(item => item.is_completed).length / jobCard.pre_procedure_checklist.length
                : 1;
              const stepsProgress = jobCard.procedure_steps?.length > 0
                ? jobCard.procedure_steps.filter(step => step.is_completed).length / jobCard.procedure_steps.length
                : 1;

              return (
                <div
                  key={jobCard.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
                >
                  {/* Card Header */}
                  <div 
                    className="p-4 cursor-pointer hover:bg-gray-50 transition"
                    onClick={() => setExpandedCard(isExpanded ? null : jobCard.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {/* Status Badge */}
                        <Badge className={`${getStatusColor(jobCard.status)} flex items-center gap-1`}>
                          {getStatusIcon(jobCard.status)}
                          {jobCard.status.replace('_', ' ')}
                        </Badge>
                        
                        {/* Payment Status Badge */}
                        {jobCard.is_paid ? (
                          <Badge className="bg-green-100 text-green-700">
                            ✓ Paid
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700">
                            Payment Pending
                          </Badge>
                        )}

                        {/* Patient Info */}
                        <div>
                          {jobCard.appointment_ref_number && (
                            <span className="text-xs font-mono bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded mr-2">
                              {jobCard.appointment_ref_number}
                            </span>
                          )}
                          <h3 className="font-semibold text-gray-900 inline">{jobCard.patient_name}</h3>
                          <div className="flex items-center gap-2">
                            <p className="text-sm text-gray-500">{jobCard.service_name}</p>
                            {user?.role === 'Admin' && jobCard.payment_amount > 0 && (
                              <span className="text-xs font-semibold text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                                ₹{jobCard.payment_amount.toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        {/* Time */}
                        <div className="text-right">
                          <div className="text-sm font-medium text-gray-700 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {jobCard.scheduled_date}
                          </div>
                          <div className="text-xs text-gray-500">
                            {jobCard.scheduled_time || 'No time'}
                          </div>
                        </div>

                        {/* Assigned Therapists */}
                        <div className="text-right min-w-[120px]">
                          {jobCard.assigned_therapist_names?.length > 0 ? (
                            <div className="flex items-center gap-1 text-sm text-purple-600">
                              <User className="w-3 h-3" />
                              {jobCard.assigned_therapist_names.join(', ')}
                            </div>
                          ) : (
                            <Badge variant="outline" className="text-orange-600 border-orange-200">
                              Unassigned
                            </Badge>
                          )}
                        </div>

                        {/* Assign Button */}
                        {jobCard.status !== 'completed' && jobCard.status !== 'cancelled' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAssignTherapist(jobCard);
                            }}
                          >
                            <UserPlus className="w-4 h-4 mr-1" />
                            {jobCard.assigned_therapist_ids?.length > 0 ? 'Reassign' : 'Assign'}
                          </Button>
                        )}

                        {/* Progress Bars */}
                        <div className="w-24">
                          {jobCard.pre_procedure_checklist?.length > 0 && (
                            <div className="mb-1">
                              <div className="flex items-center justify-between text-xs text-gray-500 mb-0.5">
                                <span>Checklist</span>
                                <span>{Math.round(checklistProgress * 100)}%</span>
                              </div>
                              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-purple-500 rounded-full transition-all"
                                  style={{ width: `${checklistProgress * 100}%` }}
                                />
                              </div>
                            </div>
                          )}
                          {jobCard.procedure_steps?.length > 0 && (
                            <div>
                              <div className="flex items-center justify-between text-xs text-gray-500 mb-0.5">
                                <span>Steps</span>
                                <span>{Math.round(stepsProgress * 100)}%</span>
                              </div>
                              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-green-500 rounded-full transition-all"
                                  style={{ width: `${stepsProgress * 100}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Expand Icon */}
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 p-4 bg-gray-50">
                      <div className="grid grid-cols-2 gap-6">
                        {/* Checklist Section */}
                        <div>
                          <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-purple-600" />
                            Pre-Procedure Checklist
                          </h4>
                          {jobCard.pre_procedure_checklist?.length > 0 ? (
                            <div className="space-y-2">
                              {jobCard.pre_procedure_checklist.map(item => (
                                <div 
                                  key={item.id}
                                  className={`flex items-center justify-between p-2 rounded-lg ${
                                    item.is_completed ? 'bg-green-50 border border-green-200' : 'bg-white border border-gray-200'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    {item.is_completed ? (
                                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                                    ) : (
                                      <Circle className="w-4 h-4 text-gray-300" />
                                    )}
                                    <span className={item.is_completed ? 'text-green-700' : 'text-gray-700'}>
                                      {item.description}
                                    </span>
                                  </div>
                                  {!item.is_completed && jobCard.status === 'in_progress' && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-purple-600 hover:bg-purple-50"
                                      onClick={() => handleCompleteChecklist(jobCard.id, item.id)}
                                    >
                                      <CheckCheck className="w-4 h-4" />
                                    </Button>
                                  )}
                                  {item.is_completed && (
                                    <div className="text-right">
                                      <span className="text-xs text-green-600 font-medium block">
                                        {item.completed_by_therapist_name || 'Completed'}
                                      </span>
                                      <span className="text-xs text-gray-400">
                                        {formatTime(item.completed_at)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-400">No checklist items</p>
                          )}
                        </div>

                        {/* Procedure Steps Section */}
                        <div>
                          <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <ClipboardList className="w-4 h-4 text-green-600" />
                            Procedure Steps
                          </h4>
                          {jobCard.procedure_steps?.length > 0 ? (
                            <div className="space-y-2">
                              {jobCard.procedure_steps.map((step, stepIndex) => {
                                const canComplete = stepIndex === 0 || jobCard.procedure_steps[stepIndex - 1]?.is_completed;
                                
                                return (
                                  <div 
                                    key={step.id}
                                    className={`flex items-center justify-between p-2 rounded-lg ${
                                      step.is_completed 
                                        ? 'bg-green-50 border border-green-200' 
                                        : 'bg-white border border-gray-200'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                                        step.is_completed 
                                          ? 'bg-green-600 text-white' 
                                          : 'bg-gray-200 text-gray-600'
                                      }`}>
                                        {step.is_completed ? <CheckCheck className="w-3 h-3" /> : step.order}
                                      </span>
                                      <div>
                                        <span className={step.is_completed ? 'text-green-700' : 'text-gray-700'}>
                                          {step.description}
                                        </span>
                                        {step.time_taken_minutes !== undefined && step.time_taken_minutes !== null && (
                                          <span className="text-xs text-gray-500 ml-2">
                                            ({step.time_taken_minutes.toFixed(1)} min)
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {!step.is_completed && jobCard.status === 'in_progress' && canComplete && (
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="text-green-600 hover:bg-green-50"
                                          onClick={() => handleCompleteStep(jobCard.id, step.id)}
                                        >
                                          <CheckCheck className="w-4 h-4 mr-1" />
                                          Done
                                        </Button>
                                      )}
                                      {!step.is_completed && jobCard.status === 'in_progress' && !canComplete && (
                                        <span className="text-xs text-gray-400">Complete previous step first</span>
                                      )}
                                      {step.is_completed && (
                                        <div className="text-right">
                                          <span className="text-xs text-green-600 font-medium block">
                                            {step.completed_by_therapist_name || 'Completed'}
                                          </span>
                                          <span className="text-xs text-gray-400">
                                            {formatTime(step.completed_at)}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-400">No procedure steps</p>
                          )}
                        </div>
                      </div>

                      {/* Close Job Card Button */}
                      {jobCard.status === 'in_progress' && (
                        <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end">
                          <Button
                            onClick={() => handleCloseJobCard(jobCard.id)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Close Job Card & Checkout Patient
                          </Button>
                        </div>
                      )}

                      {/* Additional Info */}
                      <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Created:</span>
                          <span className="ml-2 text-gray-700">{formatTime(jobCard.created_at)}</span>
                        </div>
                        {jobCard.started_at && (
                          <div>
                            <span className="text-gray-500">Started:</span>
                            <span className="ml-2 text-gray-700">{formatTime(jobCard.started_at)}</span>
                          </div>
                        )}
                        {jobCard.completed_at && (
                          <div>
                            <span className="text-gray-500">Completed:</span>
                            <span className="ml-2 text-gray-700">{formatTime(jobCard.completed_at)}</span>
                          </div>
                        )}
                        <div>
                          <span className="text-gray-500">Created by:</span>
                          <span className="ml-2 text-gray-700">{jobCard.created_by_user_name}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {assignDialog}
    </Layout>
  );
};

export default JobCardsPage;
